import { ChangeDetectionStrategy, Component, effect, inject } from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DialogComponent, InputErrorsComponent, controlMarkAsTouched } from '@comparit/core';
import { KundeLoginResponse } from '@comparit/common-api';
import { LoginDialogData } from '../../../../services/kunde-login-dialog.service';
import { mlpEnvironment } from '../mlp-environment';
import { mlpPortalDashboardUrl } from '../mlp-portal.helper';
import { MlpLoginDialogFacade } from './mlp-login-dialog.facade';

@Component({
  selector: 'app-mlp-login-dialog',
  templateUrl: './mlp-login-dialog.component.html',
  styleUrl: './mlp-login-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MlpLoginDialogFacade],
  imports: [
    DialogComponent,
    MatButtonModule,
    MatIconModule,
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    InputErrorsComponent,
  ],
})
export class MlpLoginDialogComponent {
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly dialogRef =
    inject<MatDialogRef<MlpLoginDialogComponent, KundeLoginResponse | null>>(MatDialogRef);
  protected readonly facade = inject(MlpLoginDialogFacade);
  protected readonly data = inject<LoginDialogData>(MAT_DIALOG_DATA);

  protected readonly benutzername = this.fb.control('', [Validators.required]);
  protected readonly kennwort = this.fb.control('', [Validators.required]);

  /** MLP's own customer portal, separate from the zeitsprung KFZ portal (KFZ-2718). */
  protected readonly financialHomeUrl = 'https://kundenportal.mlp.de/';
  protected readonly portalDashboardUrl = mlpPortalDashboardUrl;
  protected readonly registrationUrl = mlpEnvironment.registrationUrl;
  protected readonly passwordResetUrl = mlpEnvironment.passwordResetUrl;

  constructor() {
    // A gated login has a caller waiting on it — a task restore, or a request
    // the interceptor is about to retry — so hand the response back the moment
    // login succeeds. Showing the success screen would offer "Zum Kundenportal",
    // navigating the user away from whatever they were in the middle of.
    effect(() => {
      const response = this.facade.login.data();
      if (response && this.data.mode === 'gated') {
        this.dialogRef.close(response);
      }
    });
  }

  protected submit(): void {
    controlMarkAsTouched(this.benutzername);
    controlMarkAsTouched(this.kennwort);

    if (this.benutzername.invalid || this.kennwort.invalid) return;

    this.facade.submit(this.benutzername.value, this.kennwort.value);
  }

  protected onClose(): void {
    this.dialogRef.close(this.facade.login.data() ?? null);
  }
}
